import { hasPlusOrProPlan, hasPlusPlan, hasProPlan } from "./retailerStoreLimits";

export type PlanTier = "basic" | "plus" | "pro";

/** What a paid user tapped while locked: a retailer slug or the Best Deals feed. */
export type LockedItem = string;
export const BEST_DEALS_ITEM = "hot_deals";

export const PLAN_NAME: Record<PlanTier, string> = { basic: "Basic", plus: "Plus", pro: "Pro" };

const lowerPlans = (userRetailers: string[] | undefined | null) =>
  (userRetailers ?? []).map((plan) => plan.toLowerCase());

/**
 * Which plan a paid user has, and which one unlocks what they tapped (mirrors
 * pinto's planUpgradeTarget): Basic + a store → Plus, Basic + Best Deals → Pro,
 * Plus (anything) → Pro.
 */
export function planUpgradeTarget(userRetailers: string[] | undefined | null, locked: LockedItem) {
  const plans = lowerPlans(userRetailers);
  const current: PlanTier = hasProPlan(plans) ? "pro" : hasPlusPlan(plans) ? "plus" : "basic";
  const target: PlanTier = locked === BEST_DEALS_ITEM || current !== "basic" ? "pro" : "plus";
  return { current, target };
}

/** Plan named in the "More stores with {plan}" header: Basic unlocks more with Plus, Plus only has Pro left. */
export function moreStoresPlanName(userRetailers: string[] | undefined | null): string {
  return hasPlusOrProPlan(lowerPlans(userRetailers)) ? PLAN_NAME.pro : PLAN_NAME.plus;
}

/** Best Deals is a Pro feed (same rule as its page): admins or a Pro plan. */
export function hasBestDealsAccess(
  user: { admin?: boolean; retailers?: string[] | null } | null | undefined,
): boolean {
  if (!user) return false;
  if (user.admin === true) return true;
  return hasProPlan(lowerPlans(user.retailers));
}

export type ComparisonRowId = "chains" | "stores" | "extra" | "changes" | "bestDeals" | "hawkFinds";

/** One comparison row: label + the current plan's value + the next plan's value (storeMenu.* keys). */
export interface ComparisonRow {
  id: ComparisonRowId;
  label: string;
  from: string;
  to: string;
}

const row = (id: ComparisonRowId, label: string, from: string, to: string): ComparisonRow => ({
  id,
  label: `storeMenu.${label}`,
  from: `storeMenu.${from}`,
  to: `storeMenu.${to}`,
});

// Extra nearby stores (server walmartextra / homedepotextra SlotsForUser):
// Basic 4, Plus 7, Pro 15 at Walmart and 10 at Home Depot. Only those two
// chains get extras; the range covers both when the sheet is about another one.
function proExtraKey(locked?: LockedItem): string {
  if (locked === "walmart") return "proExtraWalmart";
  if (locked === "home_depot") return "proExtraHomeDepot";
  return "proExtra";
}

export function comparisonRows(current: PlanTier, target: PlanTier, locked?: LockedItem): ComparisonRow[] {
  if (target === "plus") {
    return [
      row("chains", "rowChains", "basicChains", "plusChains"),
      row("stores", "rowStores", "basicStores", "plusStores"),
      row("extra", "rowExtra", "basicExtra", "plusExtra"),
      row("changes", "rowChanges", "basicChanges", "plusChanges"),
    ];
  }
  return [
    ...(current === "basic" ? [row("chains", "rowChains", "basicChains", "plusChains")] : []),
    row("stores", "rowStores", current === "basic" ? "basicStores" : "plusStores", "proStores"),
    row("extra", "rowExtra", current === "basic" ? "basicExtra" : "plusExtra", proExtraKey(locked)),
    row("bestDeals", "rowBestDeals", "notIncluded", "included"),
    row("hawkFinds", "rowHawkFinds", "notIncluded", "included"),
  ];
}
